import {
  Box,
  Container,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';

import MixImportPanel from './components/MixImportPanel';
import MixDashboardCards from './components/MixDashboardCards';
import MixDeliveriesTable from './components/MixDeliveriesTable';
import MixStockSummary from './components/MixStockSummary';
import AddMissingProductDialog from './components/AddMissingProductDialog';

import { MIX_IMPORT_STATUS } from './types/mix-deliveries.types';

import { resumeMixImport } from '../../../api/mix-deliveriesApi';

const MIX_TABS = {
  IMPORT: 'IMPORT',
  OPERATIONS: 'OPERATIONS',
};

export default function MixDeliveriesPage({ userRole }) {
  const normalizedRole = String(userRole ?? '').toUpperCase();

  const isOperator = normalizedRole === 'OPERATOR';

  /*
   * Operator od razu trafia na listę miksów.
   * Pozostali użytkownicy domyślnie widzą import.
   */
  const [activeTab, setActiveTab] = useState(
    isOperator ? MIX_TABS.OPERATIONS : MIX_TABS.IMPORT,
  );

  const [refreshKey, setRefreshKey] = useState(0);

  const [importRefreshKey, setImportRefreshKey] = useState(0);

  const [missingProductDialog, setMissingProductDialog] = useState({
    product: null,
    importId: null,
  });

  /*
   * Zabezpieczenie na wypadek zmiany użytkownika
   * albo późniejszego wczytania roli.
   *
   * Operator nigdy nie może pozostać
   * na zakładce importu.
   */
  useEffect(() => {
    if (isOperator && activeTab !== MIX_TABS.OPERATIONS) {
      setActiveTab(MIX_TABS.OPERATIONS);
    }
  }, [isOperator, activeTab]);

  const refreshOperationalData = () => {
    setRefreshKey((value) => value + 1);
  };

  const refreshImportData = () => {
    setImportRefreshKey((value) => value + 1);
  };

  const handleAddProduct = (product, importId) => {
    setMissingProductDialog({
      product,
      importId,
    });
  };

  const handleCloseProductDialog = () => {
    setMissingProductDialog({
      product: null,
      importId: null,
    });
  };

  const handleProductSaved = async () => {
    const importId = missingProductDialog.importId;

    if (!importId) {
      handleCloseProductDialog();
      return;
    }

    const result = await resumeMixImport(importId);

    handleCloseProductDialog();
    refreshImportData();

    if (result.status === MIX_IMPORT_STATUS.COMPLETED) {
      refreshOperationalData();

      setActiveTab(MIX_TABS.OPERATIONS);
    }
  };

  const handleAnalysisCompleted = () => {
    refreshOperationalData();
    refreshImportData();

    setActiveTab(MIX_TABS.OPERATIONS);
  };

  const handleTabChange = (_event, newValue) => {
    /*
     * Dodatkowe zabezpieczenie frontendu.
     */
    if (isOperator && newValue === MIX_TABS.IMPORT) {
      return;
    }

    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Paper
          variant="outlined"
          sx={{
            p: {
              xs: 2,
              md: 3,
            },
          }}
        >
          <Typography variant="h4" component="h1" fontWeight={700}>
            Zarządzanie miksami
          </Typography>

          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {isOperator
              ? 'Obsługa miksów do przygotowania.'
              : 'Import zapasu, analiza dostaw oraz obsługa pozycji miksowych.'}
          </Typography>
        </Paper>

        <Paper variant="outlined">
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {!isOperator && (
              <Tab value={MIX_TABS.IMPORT} label="Import danych" />
            )}

            <Tab value={MIX_TABS.OPERATIONS} label="Miksy do przygotowania" />
          </Tabs>
        </Paper>

        {!isOperator && activeTab === MIX_TABS.IMPORT && (
          <MixImportPanel
            refreshKey={importRefreshKey}
            onAddProduct={handleAddProduct}
            onAnalysisCompleted={handleAnalysisCompleted}
          />
        )}

        {activeTab === MIX_TABS.OPERATIONS && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Miksy do przygotowania
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Dostawy 8AAA zawierające pozycje wymagające przygotowania w
                strefie kompletacji.
              </Typography>
            </Box>

            <MixDashboardCards refreshKey={refreshKey} />

            <MixStockSummary refreshKey={refreshKey} />

            <MixDeliveriesTable
              refreshKey={refreshKey}
              userRole={normalizedRole}
              onDataChanged={refreshOperationalData}
            />
          </Stack>
        )}

        {!isOperator && (
          <AddMissingProductDialog
            open={Boolean(missingProductDialog.product)}
            product={missingProductDialog.product}
            onClose={handleCloseProductDialog}
            onSaved={handleProductSaved}
          />
        )}
      </Stack>
    </Container>
  );
}
